import { useState } from 'react';
import { X } from 'lucide-react';
import type { AnalyzeSettings } from '../../types';
import { DEFAULT_ANALYZE_SETTINGS } from '../../types';

interface AnalyzeSettingsModalProps {
  settings: AnalyzeSettings;
  onApply: (settings: AnalyzeSettings) => void;
  onClose: () => void;
}

export default function AnalyzeSettingsModal({ settings, onApply, onClose }: AnalyzeSettingsModalProps) {
  const [local, setLocal] = useState<AnalyzeSettings>({ ...settings });

  const handleReset = () => setLocal({ ...DEFAULT_ANALYZE_SETTINGS });

  const handleApply = () => {
    onApply(local);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Analysis Settings</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5">
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-700 dark:text-gray-300">Cluster Radius</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{local.radiusMiles} mi</span>
            </div>
            <input type="range" min={25} max={150} step={5} value={local.radiusMiles}
              onChange={(e) => setLocal({ ...local, radiusMiles: Number(e.target.value) })}
              className="w-full accent-blue-500" />
            <div className="flex justify-between text-xs text-gray-400"><span>25 mi</span><span>150 mi</span></div>
          </div>

          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-700 dark:text-gray-300">Minimum Schools</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{local.minSchools}</span>
            </div>
            <input type="range" min={10} max={200} step={5} value={local.minSchools}
              onChange={(e) => setLocal({ ...local, minSchools: Number(e.target.value) })}
              className="w-full accent-blue-500" />
            <div className="flex justify-between text-xs text-gray-400"><span>10</span><span>200</span></div>
          </div>

          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-700 dark:text-gray-300">Max Blast Penetration</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{local.maxBlastPct}%</span>
            </div>
            <input type="range" min={0} max={50} step={1} value={local.maxBlastPct}
              onChange={(e) => setLocal({ ...local, maxBlastPct: Number(e.target.value) })}
              className="w-full accent-blue-500" />
            <div className="flex justify-between text-xs text-gray-400"><span>0%</span><span>50%</span></div>
          </div>

          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-700 dark:text-gray-300">Priority Balance</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {local.weightMode <= 0.3 ? 'Size' : local.weightMode >= 0.7 ? 'Penetration' : 'Balanced'}
              </span>
            </div>
            <input type="range" min={0} max={1} step={0.1} value={local.weightMode}
              onChange={(e) => setLocal({ ...local, weightMode: Number(e.target.value) })}
              className="w-full accent-blue-500" />
            <div className="flex justify-between text-xs text-gray-400"><span>Size Priority</span><span>Low Penetration Priority</span></div>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700 dark:text-gray-300">Include Competitor Schools</span>
            <button
              onClick={() => setLocal({ ...local, includeCompetitor: !local.includeCompetitor })}
              className={`flex items-center gap-1.5 rounded-full px-3 py-1 transition-all cursor-pointer ${
                local.includeCompetitor
                  ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                  : 'bg-gray-100 dark:bg-gray-700/50 text-gray-400 dark:text-gray-500 opacity-60'
              }`}
            >
              <span className={`h-2.5 w-2.5 rounded-full ${local.includeCompetitor ? 'bg-red-500' : 'bg-gray-300 dark:bg-gray-600'}`} />
              <span className="text-sm font-medium">{local.includeCompetitor ? 'On' : 'Off'}</span>
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button onClick={handleReset}
            className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors">
            Reset to Defaults
          </button>
          <button onClick={handleApply}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors">
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}
